/**
 * «Наши фото»: платья из рабочего чата, разложенные по папкам. Папку собирает
 * кластеризация по сходству, имя даёт человек; правка папок — на экране папки.
 *
 * Фильтры «отправитель» и «регион» — одиночные фишки, живут в адресе
 * (`?sender=493&region=ЕКБ`): ссылкой на срез можно поделиться, и «назад» из
 * папки возвращает в тот же срез. Считаются на клиенте по одному ответу; число
 * у фишки — сколько папок с этим отправителем (регионом) в срезе другого
 * фильтра.
 *
 * Файлы, что не легли в справочник (больше 20 МБ или не скачались), — плашкой
 * сверху: их нет в папках, но о них надо знать.
 */
import { useQuery } from "@tanstack/react-query";
import { Download, ImageOff, Trophy } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { CrmTopImportDialog } from "@/components/photos/CrmTopImportDialog";
import { CrmImportDialog } from "@/components/photos/CrmImportDialog";
import { RetryImg } from "@/components/trends/PinGrid";
import { btnPrimary, Chip, Empty, ErrorLine, Loading, Panel } from "@/components/trends/ui";
import { plural } from "@/lib/library";
import { mediaUrl, num } from "@/lib/media";
import {
  dressTitle,
  loadPhotos,
  photoKeys,
  surname,
  type DressCard,
  type FailedMention,
  type PhotosList,
} from "@/lib/photos";
import { retryQuery } from "@/lib/trends";

export function PhotosPage() {
  const list = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos, retry: retryQuery });

  if (list.isPending) return <Loading />;
  if (list.isError)
    return (
      <div className="p-5">
        <ErrorLine error={list.error} />
      </div>
    );
  return <PhotosView d={list.data} />;
}

const hasSender = (d: DressCard, id: number | null) => id === null || d.senders.some((s) => s.user_id === id);
const hasRegion = (d: DressCard, short: string | null) => short === null || d.regions.includes(short);

function PhotosView({ d }: { d: PhotosList }) {
  const [params, setParams] = useSearchParams();
  const senderParam = Number(params.get("sender"));
  const sender = Number.isInteger(senderParam) && senderParam > 0 ? senderParam : null;
  const region = params.get("region") || null;

  const setFilter = (key: "sender" | "region", value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );

  const photoTotal = useMemo(() => d.dresses.reduce((sum, x) => sum + x.count, 0), [d.dresses]);
  // Число у фишки — по срезу ДРУГОГО фильтра: иначе выбранный регион обнулял бы все прочие.
  const byRegion = useMemo(() => d.dresses.filter((x) => hasRegion(x, region)), [d.dresses, region]);
  const bySender = useMemo(() => d.dresses.filter((x) => hasSender(x, sender)), [d.dresses, sender]);
  const shown = useMemo(() => byRegion.filter((x) => hasSender(x, sender)), [byRegion, sender]);
  const senderCount = (id: number) => byRegion.filter((x) => hasSender(x, id)).length;
  const regionCount = (short: string) => bySender.filter((x) => hasRegion(x, short)).length;

  const [crmOpen, setCrmOpen] = useState(false);
  const [topOpen, setTopOpen] = useState(false);

  return (
    <div className="grid gap-4 p-5">
      <header className="grid gap-1" data-annot="шапка наших фото">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">
            Наши фото ·{" "}
            <span className="tnum">
              {num(d.dresses.length)} {plural(d.dresses.length, "платье", "платья", "платьев")}, {num(photoTotal)} фото
            </span>
          </h1>
          <button type="button" className={`${btnPrimary} ml-auto`} onClick={() => setCrmOpen(true)}>
            <Download size={13} />
            Импорт из CRM
          </button>
          <button type="button" className={btnPrimary} onClick={() => setTopOpen(true)}>
            <Trophy size={13} />
            Импорт топ
          </button>
        </div>
        <CrmImportDialog open={crmOpen} onClose={() => setCrmOpen(false)} />
        <CrmTopImportDialog open={topOpen} onClose={() => setTopOpen(false)} />
        <p className="text-[12.5px] text-fg-muted">
          Фото платьев из рабочего чата и CRM. Папки собирает кластеризация по сходству; переименуйте папку или перенесите
          фото — это закрепит состав.
        </p>
        {d.failed.length ? <FailedNote failed={d.failed} /> : null}
      </header>

      <Panel
        title={
          <>
            Папки <span className="tnum font-normal text-fg-faint">{shown.length}</span>
          </>
        }
        annot="папки платьев"
      >
        {d.dresses.length ? (
          <div className="grid gap-2 border-b border-border px-3 py-2" data-annot="фильтры фото">
            <div className="flex flex-wrap items-center gap-1.5" data-annot="фильтр по отправителю">
              <span className="text-[11.5px] text-fg-faint">отправитель</span>
              <Chip on={sender === null} onClick={() => setFilter("sender", null)}>
                все
              </Chip>
              {d.senders.map((s) => (
                <Chip
                  key={s.user_id}
                  on={sender === s.user_id}
                  title={`${s.name}: ${num(s.photos)} фото`}
                  onClick={() => setFilter("sender", sender === s.user_id ? null : String(s.user_id))}
                >
                  {surname(s.name)} <span className="tnum text-fg-faint">{num(senderCount(s.user_id))}</span>
                </Chip>
              ))}
            </div>
            {d.regions.length ? (
              <div className="flex flex-wrap items-center gap-1.5" data-annot="фильтр по региону">
                <span className="text-[11.5px] text-fg-faint">регион</span>
                <Chip on={region === null} onClick={() => setFilter("region", null)}>
                  все
                </Chip>
                {d.regions.map((r) => (
                  <Chip
                    key={r.short}
                    on={region === r.short}
                    title={`${r.short}: ${num(r.photos)} фото`}
                    onClick={() => setFilter("region", region === r.short ? null : r.short)}
                  >
                    {r.short} <span className="tnum text-fg-faint">{num(regionCount(r.short))}</span>
                  </Chip>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {d.dresses.length === 0 ? (
          <Empty>
            Фото пока нет — их забирает из чата команда <code className="text-fg-muted">reels-runner photos pull</code>.
          </Empty>
        ) : shown.length === 0 ? (
          <Empty>Под фильтр не попала ни одна папка.</Empty>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3 p-3">
            {shown.map((x) => (
              <DressTile key={x.id} d={x} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function FailedNote({ failed }: { failed: FailedMention[] }) {
  return (
    <div
      className="grid w-fit max-w-full gap-1 rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px] text-warn"
      data-annot="не скачано"
    >
      <div className="font-medium">не скачано: {num(failed.length)}</div>
      <ul className="grid gap-0.5">
        {failed.map((f) => (
          <li key={`${f.chat_tg_id}:${f.message_tg_id}`}>
            {f.user_name} · {f.file_name ?? "фото"} ·{" "}
            {f.state === "too_big" ? "больше 20 МБ — бот Telegram такие не отдаёт" : (f.error ?? "не скачался")}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DressTile({ d }: { d: DressCard }) {
  const title = dressTitle(d);
  return (
    <Link
      to={`/photos/${d.id}`}
      className="group grid content-start overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle transition-colors hover:border-border-strong"
      data-annot="папка платья"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-bg-elevated">
        {d.cover?.thumb_path ? (
          <RetryImg
            src={mediaUrl(d.cover.thumb_path)}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="grid h-full place-items-center text-[12px] text-fg-faint">
            <span className="grid justify-items-center gap-1">
              <ImageOff size={18} />
              {d.cover ? "превью нет" : "пусто"}
            </span>
          </div>
        )}
      </div>
      <div className="grid gap-1 px-2.5 py-2">
        <div
          className={`truncate text-[13px] font-medium group-hover:text-accent-text ${d.name ? "text-fg" : "text-fg-muted"}`}
          title={title}
        >
          {title}
        </div>
        <div className="text-[11.5px] text-fg-muted tnum">{num(d.count)} фото</div>
        {d.regions.length ? (
          <div className="flex flex-wrap gap-1">
            {d.regions.map((r) => (
              <span key={r} className="rounded-[3px] bg-bg-hover px-1 text-[10.5px] leading-[16px] text-fg-muted">
                {r}
              </span>
            ))}
          </div>
        ) : null}
        {d.senders.length ? (
          <div className="truncate text-[11.5px] text-fg-faint" title={d.senders.map((s) => s.name).join(", ")}>
            {d.senders.map((s) => surname(s.name)).join(", ")}
          </div>
        ) : d.source_crm ? (
          <div className="truncate text-[11.5px] text-fg-faint">из CRM</div>
        ) : null}
      </div>
    </Link>
  );
}
